import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { scopeToSite } from '../../lib/siteScope';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { IconDownload, IconCurrencyPound } from '@tabler/icons-react';

export default function CostReport() {
  const { orgId, userProfile, role } = useAuth();
  const siteId = userProfile?.siteId;
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const snap = await getDocs(collection(db, 'organisations', orgId, 'machines'));
        const all = scopeToSite(snap.docs.map(d => ({ id: d.id, ...d.data() })), role, siteId);
        const withCosts = all.map(m => {
          const ct = m.costTracking || {};
          const total = (ct.totalServiceCost || 0) + (ct.totalRepairCost || 0) + (ct.totalFuelCost || 0) + (m.purchasePrice || 0);
          const costPerHour = m.currentHours ? +(total / m.currentHours).toFixed(2) : null;
          return { ...m, totalCost: total, costPerHour };
        }).filter(m => m.totalCost > 0).sort((a, b) => b.totalCost - a.totalCost);
        setMachines(withCosts);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const totalFleetCost = machines.reduce((sum, m) => sum + m.totalCost, 0);
  const avgCostPerHour = machines.filter(m => m.costPerHour).reduce((sum, m) => sum + m.costPerHour, 0) / (machines.filter(m => m.costPerHour).length || 1);

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title="Cost per machine report"
          subtitle="Total cost of ownership across your fleet"
          actions={<Button variant="secondary" icon={<IconDownload size={14} stroke={1.5} />} onClick={() => toast.info('PDF export coming soon')}>Export PDF</Button>}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Machines tracked', value: machines.length, colour: 'var(--color-text-primary)' },
            { label: 'Total fleet cost', value: '£' + totalFleetCost.toLocaleString(), colour: 'var(--color-text-primary)' },
            { label: 'Avg cost per hour', value: avgCostPerHour ? '£' + avgCostPerHour.toFixed(2) : '—', colour: 'var(--color-brand)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 28, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>
        {loading ? <LoadingSkeleton rows={5} card /> : machines.length === 0 ? (
          <Card><EmptyState icon={<IconCurrencyPound size={28} stroke={1.5} />} title="No cost data yet" message="Log service records and fuel fills to track costs" /></Card>
        ) : (
          <Card padding="0">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
              {['Machine', 'Service', 'Repair', 'Fuel', 'Total'].map(h => (
                <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
              ))}
            </div>
            {machines.map((m, i) => (
              <div
                key={m.id}
                onClick={() => navigate('/machines/' + m.id)}
                style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '11px 16px', borderBottom: i < machines.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{m.makeLabel} {m.modelLabel}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{m.siteName}</div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>£{(m.costTracking?.totalServiceCost || 0).toLocaleString()}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>£{(m.costTracking?.totalRepairCost || 0).toLocaleString()}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>£{(m.costTracking?.totalFuelCost || 0).toLocaleString()}</div>
                <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-primary)' }}>£{m.totalCost.toLocaleString()}</div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </Layout>
  );
}